import { useLayoutEffect, useRef } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

import { composeFormValueQueryString, getDrupalSetting, getViewedPageParams } from '@ggs/utils'
import { addNotification, useDispatch } from '@ggs/store'
import { useI18n } from '@ggs/i18n'

import { SubmitButton } from '../buttons'
import { KeywordField, LocationField } from '../fields'
import { usePosition } from '../../../local/hooks'

import './QuickSearchForm.scss'

/**
 * @typedef {Object} QuickSearchNoResultsFormProps
 * @property {boolean=} embed Determine if form is embed in external page.
 */

/**
 * Quick search without results form.
 * @param {QuickSearchNoResultsFormProps} props
 * @return {JSX.Element}
 */
const QuickSearchNoResultsForm = ({ embed = false }) => {
  const { t } = useI18n()
  const formRef = useRef(null)
  /**
   * @typedef {object} formSchema
   * @property {string} searchTerm
   * @property {string} address
   */
  // Set up the form schema
  const formSchema = z.object({
    searchTerm: z
      .string()
      .trim()
      .min(1, { message: t('Error: Cannot search without a topic/keyword') }),
    address: z
      .string()
      .trim()
      .min(1, { message: t('Error: Cannot search without a location') }),
    searchDistance: z.enum(['10', '20', '50', '100', '200', '400']),
  })
  const queryStringValues = getViewedPageParams({
    searchDistance: '50',
  })
  const dispatch = useDispatch()

  // fetch the location data from the browser
  const { address } = usePosition()

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    register,
    resetField,
    setError,
    clearErrors,
    formState: { errors: formErrors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: queryStringValues,
  })

  // If address is grabbed from the browser, set the form value
  // If address is grabbed from the browser, set the form value
  useLayoutEffect(() => {
    if (address && !getValues('address')) {
      setValue('address', address)

      // Address field is not a controlled input, so we need to set the value manually.
      const addressField = formRef.current?.querySelector('input[name="address"]')
      if (addressField) {
        addressField.value = address
      }
    }
  }, [address, formRef])

  // If distance is grabbed from the browser, set the form value
  // useEffect(() => {
  //   if (queryStringValues.searchDistance && getValues('searchDistance') !== queryStringValues.searchDistance) {
  //     setValue('searchDistance', queryStringValues.searchDistance)
  //   }
  // }, [queryStringValues.searchDistance])

  /**
   * On submit handler for quick search form.
   * @param {formSchema} formValues
   * @return {Promise<void>}
   */
  const onSubmit = async (formValues) => {
    const searchParams = composeFormValueQueryString(formValues)
    const baseUrl = getDrupalSetting('baseUrl') || `${process.env.REACT_APP_DRUPAL_BASE_URL}`
    const searchResultsUrl = `${baseUrl}/search?${searchParams}`

    if (`${process.env.STORYBOOK}` === 'yes') {
      dispatch(addNotification('redirect', 'warning', `Skipping redirect to ${searchResultsUrl}`))
    } else {
      if (embed) {
        window.open(searchResultsUrl, '_blank')
      } else {
        window.location.href = searchResultsUrl
      }
    }
  }

  return (
    <div className="quick-search-form" data-style="home">
      <form
        ref={formRef}
        className="quick-search-form__form-content"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="quick-search-form__form-fields">
          <div className="quick-search-form__form-field-item">
            <KeywordField control={control} errors={formErrors} />
          </div>
          <div className="quick-search-form__form-field-item">
            <LocationField
              register={register}
              errors={formErrors}
              setValueCallback={setValue}
              resetValueCallback={resetField}
              errorCallback={setError}
              clearErrorCallback={clearErrors}
              getValues={getValues}
            />
          </div>
        </div>
        <input type="hidden" {...register('searchDistance')} />
        <div className="quick-search-form__form-actions">
          <div className="quick-search-form__form-action-item">
            <SubmitButton isDataLoading={false} errors={formErrors} label={t('Quick Search')} />
          </div>
        </div>
      </form>
    </div>
  )
}

export default QuickSearchNoResultsForm
